/**
 * Sample trip used by the onboarding tutorial.
 *
 * Lives ONLY in memory (see demo-store.ts) — never written to Firestore.
 * The id can't collide with a real trip: Firestore auto-ids are 20
 * alphanumeric chars, this one has a hyphen.
 *
 * Dates are relative to "now" so the trip is always *active* — past trips
 * lock every write path (see lib/trip-readonly.ts), which would defeat a
 * hands-on tutorial.
 */

import {
  Timestamp,
  type Activity,
  type ActivityCategory,
  type ExpenseEntry,
  type ExpenseType,
  type Trip,
  type TripSummary,
} from "@travelix/core";

// Direct file import (not the "@/lib/api" barrel): the api layer imports
// this module, so going through the barrel would be circular.
import { displayNameOf } from "@/lib/api/auth-user";

export const DEMO_TRIP_ID = "demo-tour";

export function isDemoTripId(tripId: string | null | undefined): boolean {
  return tripId === DEMO_TRIP_ID;
}

/** The signed-in user, who plays the trip owner in the sample. */
export interface DemoOwner {
  uid: string;
  name: string;
  email: string;
}

/** Map a Firebase user (or anything shaped like one) to the sample's owner. */
export function demoOwnerFromUser(
  user: { uid: string; displayName: string | null; email: string | null } | null,
): DemoOwner {
  return {
    uid: user?.uid ?? "demo-you",
    name: displayNameOf(user),
    email: user?.email ?? "you@example.com",
  };
}

const SOFIA = { uid: "demo-sofia", name: "Sofía", email: "sofia@example.com" };
const LEO = { uid: "demo-leo", name: "Leo", email: "leo@example.com" };

const TRIP_NAME = "Lisbon long weekend";
const CURRENCY = "EUR";

/** Local midnight `offset` days from today. */
function dayAt(offset: number, hours = 0, minutes = 0): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + offset);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

const ts = (d: Date) => Timestamp.fromDate(d);

export function buildDemoTrip(owner: DemoOwner): Trip {
  const now = Timestamp.now();
  // Started yesterday, ends in three days → "active".
  const startDate = ts(dayAt(-1));
  const endDate = ts(dayAt(3, 23, 59));
  const people = [owner, SOFIA, LEO];

  const equalSplits = (amount: number) => {
    const share = Math.round((amount / people.length) * 100) / 100;
    return people.map((p) => ({
      userId: p.uid,
      userName: p.name,
      amount: share,
      percentage: null,
      currency: CURRENCY,
    }));
  };

  const expense = (
    id: string,
    name: string,
    amount: number,
    type: ExpenseType,
    payer: { uid: string; name: string },
    dayOffset: number,
  ): ExpenseEntry => ({
    id,
    name,
    amount,
    currency: CURRENCY,
    expenseDate: ts(dayAt(dayOffset, 12)),
    type,
    creatorId: payer.uid,
    creatorName: payer.name,
    payerUid: payer.uid,
    payerName: payer.name,
    splitMode: "equal",
    splits: equalSplits(amount),
    createdAt: now,
    updatedAt: now,
  });

  return {
    id: DEMO_TRIP_ID,
    name: TRIP_NAME,
    destinations: ["Lisbon", "Sintra"],
    startDate,
    endDate,
    durationDays: Math.ceil((endDate.toMillis() - startDate.toMillis()) / 86_400_000),
    coverImage: null,
    currency: CURRENCY,
    budget: { amount: 1200, currency: CURRENCY },
    memberIds: people.map((p) => p.uid),
    members: Object.fromEntries(
      people.map((p, i) => [
        p.uid,
        {
          role: i === 0 ? "owner" : "traveler",
          name: p.name,
          email: p.email,
          addedAt: now,
        },
      ]),
    ),
    pendingInviteEmails: [],
    expenses: [
      expense("demo-exp-apartment", "Apartment in Alfama", 480, "hotels", owner, -1),
      expense("demo-exp-dinner", "Dinner at Time Out Market", 96, "restaurants", SOFIA, -1),
      expense("demo-exp-train", "Train to Sintra", 27, "transportation", LEO, 0),
    ],
    ideas: [
      {
        id: "demo-idea-belem",
        text: "Pastéis de Belém run",
        addedBy: SOFIA.uid,
        addedByName: SOFIA.name,
        addedAt: now,
      },
      {
        id: "demo-idea-lxf",
        text: "Browse LX Factory",
        addedBy: LEO.uid,
        addedByName: LEO.name,
        addedAt: now,
      },
    ],
    polls: [
      {
        id: "demo-poll-dinner",
        question: "Where should we eat tonight?",
        options: [
          { id: "seafood", label: "Seafood in Cais do Sodré" },
          { id: "fado", label: "Fado dinner in Alfama" },
          { id: "rooftop", label: "Rooftop tapas" },
        ],
        // A tie — the user's vote breaks it.
        votes: { [SOFIA.uid]: "fado", [LEO.uid]: "seafood" },
        createdBy: SOFIA.uid,
        createdByName: SOFIA.name,
        createdAt: now,
      },
    ],
    packingItems: [
      {
        id: "demo-pack-sunscreen",
        name: "Sunscreen",
        addedBy: LEO.uid,
        addedByName: LEO.name,
        addedAt: now,
        claimedBy: LEO.uid,
        claimedByName: LEO.name,
        claimedAt: now,
      },
      {
        id: "demo-pack-charger",
        name: "Portable charger",
        addedBy: owner.uid,
        addedByName: owner.name,
        addedAt: now,
      },
      {
        id: "demo-pack-towels",
        name: "Beach towels",
        addedBy: SOFIA.uid,
        addedByName: SOFIA.name,
        addedAt: now,
      },
    ],
    days: {},
    createdAt: now,
    updatedAt: now,
  };
}

export function buildDemoActivities(owner: DemoOwner): Activity[] {
  const now = Timestamp.now();
  const activity = (
    id: string,
    name: string,
    category: ActivityCategory,
    dayOffset: number,
    startH: number,
    durationHours: number,
    place: string,
    planner: { uid: string; name: string },
  ): Activity => {
    const start = dayAt(dayOffset, startH);
    const end = new Date(start.getTime() + durationHours * 3_600_000);
    return {
      id,
      name,
      description: null,
      place,
      startTime: ts(start),
      endTime: ts(end),
      durationHours,
      latitude: null,
      longitude: null,
      category,
      plannerId: planner.uid,
      plannerName: planner.name,
      createdAt: now,
      updatedAt: now,
    };
  };

  return [
    activity("demo-act-checkin", "Check in to the apartment", "stay", -1, 15, 1, "Alfama, Lisbon", owner),
    activity("demo-act-market", "Dinner at Time Out Market", "food", -1, 20, 2, "Cais do Sodré", SOFIA),
    activity("demo-act-tram", "Ride Tram 28", "place", 0, 10, 2, "Martim Moniz", LEO),
    activity("demo-act-sunset", "Sunset at Senhora do Monte", "photo", 0, 19, 1, "Graça, Lisbon", SOFIA),
    activity("demo-act-pena", "Day trip to Pena Palace", "place", 1, 9, 6, "Sintra", owner),
    activity("demo-act-cascais", "Beach afternoon", "beach", 2, 14, 4, "Cascais", LEO),
  ];
}

/** Dashboard card shape for the sample trip. */
export function toDemoSummary(trip: Trip): TripSummary {
  return {
    id: trip.id,
    name: trip.name,
    city: trip.destinations[0] ?? "",
    startDate: trip.startDate,
    endDate: trip.endDate,
    miniature: trip.coverImage,
    role: "owner",
  };
}
